import { createAsyncThunk } from '@reduxjs/toolkit'
import request, { getAxiosRequestConfig, newRequest } from 'utils/request.ts'
import { Methods, NewResponse } from 'types/request.ts'
import {
  DeleteExtraImagePayload,
  GetContractHistorySuccessPayload,
  GetContractsPayload,
  GetContractsSuccessPayload,
  GetProducersSuccessPayload,
  GetProducerSuccessPayload,
  UpdateContractsPayload,
  UploadImagePayload,
  UploadImagesSuccessPayload
} from 'store/producer/thunks/types.ts'

// GET
export const getContracts = createAsyncThunk<GetContractsSuccessPayload, GetContractsPayload>(
  'producer/getContracts',
  async ({ producerID }, thunkAPI) => {
    try {
      const { success: historySuccess, data: historyData }: NewResponse<GetContractHistorySuccessPayload> = await newRequest(
        getAxiosRequestConfig(Methods.GET, '/contracts/history?updatedToday=true')
      )

      const {
        success: producerContractsSuccess,
        data: producerContractsData,
        message: producerContractsMessage
      }: NewResponse<GetContractsSuccessPayload> = await newRequest(
        getAxiosRequestConfig(Methods.GET, `/producers/${producerID}/contracts`)
      )

      if (historySuccess && producerContractsSuccess && historyData && producerContractsData) {
        const contractHistoryIds = historyData.contractsHistory.map((contractHistory) => contractHistory.contractId)
        const enrichedContracts = producerContractsData.contracts.map((contract) => ({
          ...contract,
          disabled: contractHistoryIds.includes(contract.contractID)
        }))
        return { contracts: enrichedContracts }
      } else if (producerContractsSuccess && producerContractsData) {
        return { contracts: producerContractsData.contracts }
      } else {
        return thunkAPI.rejectWithValue(producerContractsMessage ?? 'getContracts')
      }
    } catch (err) {
      const { message } = err as Error
      return thunkAPI.rejectWithValue(message)
    }
  }
)

export const getProducers = createAsyncThunk<GetProducersSuccessPayload>('producer/getProducers', async (_, thunkAPI) => {
  try {
    const { success, data, message } = await request(getAxiosRequestConfig(Methods.GET, '/producers?withExtraImages=true'))

    if (!success) {
      return thunkAPI.rejectWithValue(message ?? 'getProducers')
    }

    return { producers: data.producers }
  } catch (err) {
    const { message } = err as Error
    return thunkAPI.rejectWithValue(message)
  }
})

export const getProducer = createAsyncThunk<GetProducerSuccessPayload, string>('producer/getProducer', async (slug, thunkAPI) => {
  try {
    const { success, data, message } = await request(getAxiosRequestConfig(Methods.GET, `/producers/${slug}?withExtraImages=true`))

    if (!success) {
      return thunkAPI.rejectWithValue(message ?? 'getProducer')
    }

    return { producer: data.producer }
  } catch (err) {
    const { message } = err as Error
    return thunkAPI.rejectWithValue(message)
  }
})

// UPDATE
export const uploadImages = createAsyncThunk<UploadImagesSuccessPayload, UploadImagePayload>(
  'producer/uploadImages',
  async ({ producerId, fieldName, files }, thunkAPI) => {
    try {
      const formData = new FormData()
      formData.append('type', fieldName)

      for (const file of files) {
        formData.append('producerImages', file)
      }

      const options = {
        method: Methods.POST,
        url: `/producers/${producerId}/images`,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }

      const { success, message, data } = await request(options)

      if (!success) {
        return thunkAPI.rejectWithValue(message ?? 'uploadImages')
      }

      return { producerId, fieldName, urls: data.urls }
    } catch (err) {
      const { message } = err as Error
      return thunkAPI.rejectWithValue(message)
    }
  }
)

export const updateContracts = createAsyncThunk<void, UpdateContractsPayload>(
  'producer/updateContracts',
  async ({ updateContractProducerData, producerID }, thunkAPI) => {
    try {
      const { success, message } = await newRequest(getAxiosRequestConfig(Methods.PATCH, '/contracts/producer', updateContractProducerData))

      if (!success) {
        return thunkAPI.rejectWithValue(message ?? 'updateContracts')
      }

      thunkAPI.dispatch(getContracts({ producerID }))
    } catch (err) {
      const { message } = err as Error
      return thunkAPI.rejectWithValue(message)
    }
  }
)

// DELETE
export const deleteExtraImage = createAsyncThunk<DeleteExtraImagePayload, DeleteExtraImagePayload>(
  'producer/deleteExtraImage',
  async ({ producerId, url }, thunkAPI) => {
    try {
      const { success, message } = await request(getAxiosRequestConfig(Methods.DELETE, `/producers/${producerId}/image`, { url }))
      if (!success) {
        return thunkAPI.rejectWithValue(message ?? 'getProducers')
      }

      return { producerId, url }
    } catch (err) {
      const { message } = err as Error
      return thunkAPI.rejectWithValue(message)
    }
  }
)
